import { MutableRefObject, useLayoutEffect } from 'react';
import { useTexture } from '@react-three/drei';
import { BufferAttribute, MathUtils, Mesh, MeshPhysicalMaterial, ShapeGeometry, Vector3 } from 'three';
import { CardBounds } from '@/lib/types';
import scratchedMetalImg from '@/img/roughness-scratched-metal-1024.png';

export const useScratchedMetalRoughness = (
    mesh: MutableRefObject<Mesh>,
    material: MutableRefObject<MeshPhysicalMaterial>,
    geometry: MutableRefObject<ShapeGeometry>,
    bounds: CardBounds | undefined,
    update: boolean
): void => {
    const scratchedMetal = useTexture(scratchedMetalImg);
    useLayoutEffect(() => {
        if (update && bounds) {
            const [xMin, xMax] = bounds.x;
            const [yMin, yMax] = bounds.y;
            const validBounds = xMin !== 0 || xMax !== 0;
            if (geometry.current && material.current && mesh.current && validBounds) {
                const worldPosition = new Vector3();
                const worldScale = new Vector3();
                mesh.current.getWorldPosition(worldPosition);
                mesh.current.getWorldScale(worldScale);
                const newUv = [];
                const position = geometry.current.getAttribute('position');
                for (let i = 0; i < position.count; i++) {
                    const index = 3 * i;

                    const x = position.array[index];
                    const worldX = worldPosition.x + x * worldScale.x;
                    newUv.push(MathUtils.mapLinear(worldX, xMin, xMax, 0, 1));

                    const y = position.array[index + 1];
                    const worldY = worldPosition.y + y * worldScale.y;
                    newUv.push(MathUtils.mapLinear(worldY, yMin, yMax, 0, 1));
                }
                geometry.current.setAttribute('uv', new BufferAttribute(new Float32Array(newUv), 2));

                material.current.roughnessMap = scratchedMetal;
            }
        }
    }, [bounds, geometry, material, mesh, scratchedMetal, update]);
};
